import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor - aggiunge il token e blocca write per readonly
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('xsophia_token')
    if (token) config.headers.Authorization = `Bearer ${token}`

    const user = JSON.parse(localStorage.getItem('xsophia_user') || 'null')
    if (user?.role === 'readonly') {
      const method = (config.method || 'get').toUpperCase()
      // La gestione della propria 2FA è un'azione di sicurezza personale,
      // sempre consentita anche per gli utenti in sola lettura.
      const isGestione2Fa = (config.url || '').includes('/auth/2fa/')
      if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && !isGestione2Fa) {
        const err = Object.assign(new Error('Utente in sola lettura'), {
          isReadonlyBlock: true,
          response: { status: 403, data: { detail: 'Utente in sola lettura: operazioni di scrittura non consentite' } },
        })
        return Promise.reject(err)
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - gestisce 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('xsophia_token')
      localStorage.removeItem('xsophia_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api

// ---- API calls ----

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  whatsappContacts: () => api.get('/auth/users/whatsapp-contacts'),
  resetPassword: (email: string) => api.post('/auth/reset-password', { email }),
  listUsers: () => api.get('/auth/users'),
  createUser: (data: unknown) => api.post('/auth/users', data),
  updateUser: (id: number, data: unknown) => api.put(`/auth/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/auth/users/${id}`),
  exportUsersXlsx: () => api.get('/auth/users/export-xlsx', { responseType: 'blob' }),
  importUsersXlsx: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/auth/users/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  registrazioni: {
    crea: (data: { email: string; nome: string; cognome: string; telefono_whatsapp?: string }) =>
      api.post('/auth/registrazioni', data),
    lista: () => api.get('/auth/registrazioni'),
    dettaglio: (token: string) => api.get(`/auth/registrazioni/${token}`),
    approva: (token: string, data: { role: string; permessi_menu: { menu_id: string; enabled: boolean }[] }) =>
      api.post(`/auth/registrazioni/${token}/approva`, data),
    rifiuta: (token: string) => api.post(`/auth/registrazioni/${token}/rifiuta`),
  },
  lockouts: {
    lista: () => api.get('/auth/lockouts'),
    sblocca: (email: string) => api.post(`/auth/lockouts/${encodeURIComponent(email)}/sblocca`),
  },
  resetPasswordRichieste: {
    lista: () => api.get('/auth/reset-password-richieste'),
    approva: (token: string) => api.post(`/auth/reset-password-richieste/${token}/approva`),
    rifiuta: (token: string) => api.post(`/auth/reset-password-richieste/${token}/rifiuta`),
  },
  totpResetRichieste: {
    lista: () => api.get('/auth/2fa-reset-richieste'),
    approva: (token: string) => api.post(`/auth/2fa-reset-richieste/${token}/approva`),
    rifiuta: (token: string) => api.post(`/auth/2fa-reset-richieste/${token}/rifiuta`),
  },
  utentiOnline: () => api.get('/auth/utenti-online'),
}

export const twoFaApi = {
  setup: () => api.post('/auth/2fa/setup'),
  verifySetup: (code: string) => api.post('/auth/2fa/verify-setup', { code }),
  disable: (code: string) => api.post('/auth/2fa/disable', { code }),
  verifyLogin: (partial_token: string, code: string) =>
    api.post('/auth/2fa/verify', { partial_token, code }),
  richiediResetTotp: (partial_token: string) =>
    api.post('/auth/2fa/reset-richiesta', { partial_token }),
}

export const reportOreTecniciApi = {
  get: (params: { anno: number; mese: number; tecnico_id?: number; inizio_fisso?: boolean; escludi_weekend?: boolean }) =>
    api.get('/report-ore-tecnici', { params }),
  exportXlsx: (params: { anno: number; mese: number; tecnico_id?: number; inizio_fisso?: boolean; escludi_weekend?: boolean }) =>
    api.get('/report-ore-tecnici/export-xlsx', { params, responseType: 'blob' }),
}

export const dashboardApi = {
  kpi: () => api.get('/dashboard/kpi'),
  andamentoMensile: (anno?: number) => api.get('/dashboard/andamento-mensile', { params: { anno } }),
  perTipologia: () => api.get('/dashboard/interventi-per-tipologia'),
  perStato: () => api.get('/dashboard/interventi-per-stato'),
  slaAlert: () => api.get('/dashboard/sla-alert'),
  perTecnico: (params?: { anno?: number; mese?: number }) =>
    api.get('/dashboard/per-tecnico', { params }),
  exportPerTecnicoXlsx: (params?: { anno?: number; mese?: number }) =>
    api.get('/dashboard/per-tecnico/export-xlsx', { params, responseType: 'blob' }),
  storicoProduzione: () =>
    api.get('/dashboard/storico-produzione'),
  materialiTecnici: (params?: { anno?: number; mese?: number }) =>
    api.get('/dashboard/materiali-tecnici', { params }),
  perRegione: () => api.get('/dashboard/per-regione'),
  agendaRed: (giorni?: number) => api.get('/dashboard/agenda-red', { params: { giorni } }),
}

export const tecniciApi = {
  list: (params?: Record<string, unknown>) => api.get('/tecnici', { params }),
  get: (id: number) => api.get(`/tecnici/${id}`),
  create: (data: unknown) => api.post('/tecnici', data),
  update: (id: number, data: unknown) => api.put(`/tecnici/${id}`, data),
  delete: (id: number, force = false) => api.delete(`/tecnici/${id}`, { params: { force } }),
  scadenzeAllarme: (giorni = 30) => api.get('/tecnici/scadenze-allarme', { params: { giorni } }),
  exportXlsx: () => api.get('/tecnici/export-xlsx', { responseType: 'blob' }),
  importXlsx: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/tecnici/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  getFoto: (id: number) => api.get(`/tecnici/${id}/foto`, { responseType: 'blob' }),
  uploadFoto: (id: number, file: File) => {
    const fd = new FormData(); fd.append('foto', file)
    return api.post(`/tecnici/${id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  deleteFoto: (id: number) => api.delete(`/tecnici/${id}/foto`),
}

export const richiesteDocApi = {
  richiedi: (tecnicoId: number, tipi: string[], note?: string) =>
    api.post(`/tecnici/${tecnicoId}/richiedi-documento`, { tipi, note }),
  miaRichiesta: () => api.get('/tecnici/mia-richiesta-documento'),
  completa: (tecnicoId: number, richiestaId: number) =>
    api.post(`/tecnici/${tecnicoId}/richieste/${richiestaId}/completa`),
  pendenti: (tecnicoId: number) => api.get(`/tecnici/${tecnicoId}/richieste-pendenti`),
  reset: (tecnicoId: number) => api.delete(`/tecnici/${tecnicoId}/richieste`),
}

export const allegatiTecniciApi = {
  list: (tecnicoId: number, tipo?: string, corsoRef?: string) =>
    api.get(`/tecnici/${tecnicoId}/allegati`, { params: { tipo, corso_ref: corsoRef } }),
  upload: (tecnicoId: number, file: File, tipo: string, corsoRef?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('tipo', tipo)
    if (corsoRef) fd.append('corso_ref', corsoRef)
    return api.post(`/tecnici/${tecnicoId}/allegati`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  download: (tecnicoId: number, allegatiId: number) =>
    api.get(`/tecnici/${tecnicoId}/allegati/${allegatiId}/download`, { responseType: 'blob' }),
  delete: (tecnicoId: number, allegatiId: number) =>
    api.delete(`/tecnici/${tecnicoId}/allegati/${allegatiId}`),
}

export const committentiApi = {
  list: (params?: Record<string, unknown>) => api.get('/committenti', { params }),
  get: (id: number) => api.get(`/committenti/${id}`),
  create: (data: unknown) => api.post('/committenti', data),
  update: (id: number, data: unknown) => api.put(`/committenti/${id}`, data),
  delete: (id: number, force = false) => api.delete(`/committenti/${id}`, { params: { force } }),
  getListino: (id: number) => api.get(`/committenti/${id}/listino`),
  addVoceListino: (id: number, data: unknown) => api.post(`/committenti/${id}/listino`, data),
  updateVoceListino: (voceId: number, data: unknown) => api.put(`/committenti/listino/${voceId}`, data),
  deleteVoceListino: (voceId: number) => api.delete(`/committenti/listino/${voceId}`),
  exportListino: (id: number) => api.get(`/committenti/${id}/listino/export-xlsx`, { responseType: 'blob' }),
  importListino: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/committenti/${id}/listino/import-xlsx`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}

export const clientiFinaliApi = {
  list: (params?: Record<string, unknown>) => api.get('/clienti-finali', { params }),
  get: (id: number) => api.get(`/clienti-finali/${id}`),
  create: (data: unknown) => api.post('/clienti-finali', data),
  update: (id: number, data: unknown) => api.put(`/clienti-finali/${id}`, data),
  delete: (id: number) => api.delete(`/clienti-finali/${id}`),
  bulkDelete: (ids: number[]) => api.post('/clienti-finali/bulk-delete', { ids }),
  deleteAll: () => api.delete('/clienti-finali'),
  geocodeMissing: () => api.post('/clienti-finali/geocode-missing'),
  geocodeOne: (id: number) => api.post(`/clienti-finali/${id}/geocode`),
  bulkGeocode: (ids: number[]) => api.post('/clienti-finali/bulk-geocode', { ids }),
  syncFromPratiche: () => api.post('/clienti-finali/sync-from-pratiche'),
  patchAddress: (id: number, data: unknown) => api.patch(`/clienti-finali/${id}/address`, data),
}

export const workOrdersApi = {
  list: (params?: Record<string, unknown>) => api.get('/work-orders', { params }),
  get: (id: number) => api.get(`/work-orders/${id}`),
  geocode: (id: number) => api.post(`/work-orders/${id}/geocode`),
  create: (data: unknown) => api.post('/work-orders', data),
  update: (id: number, data: unknown) => api.put(`/work-orders/${id}`, data),
  updateStato: (id: number, stato: string, note?: string, fatturabileManuale?: boolean) =>
    api.put(`/work-orders/${id}/stato`, null, { params: { stato, note, fatturabile_manuale: fatturabileManuale } }),
  assegna: (id: number, data: unknown) => api.put(`/work-orders/${id}/assegna`, data),
  notifyWhatsapp: (id: number) => api.post(`/work-orders/${id}/notify-whatsapp`),
  stats: () => api.get('/work-orders/stats/dashboard'),
  bulkUpdate: (data: { ids: number[]; stato?: string; tecnico_id?: number; tipologia?: string; data_appuntamento?: string; slot_orario?: string; mantieni_slot?: boolean }) => api.post('/work-orders/bulk-update', data),
  verificaFrazionarioPoste: (ids: number[]) => api.post('/work-orders/verifica-frazionario-poste', { ids }),
  verificaPosteIt: (ids: number[]) => api.post('/work-orders/verifica-poste-it', { ids }),
  gpsPosteIt: (ids: number[]) => api.post('/work-orders/gps-poste-it', { ids }),
  applicaCorrezioni: (correzioni: {
    wo_id: number; comune?: string; indirizzo?: string; cap?: string
    provincia?: string; regione?: string; codice_sito?: string; denominazione_sito?: string
  }[], motivo?: string) =>
    api.post('/work-orders/applica-correzioni-frazionario', { correzioni, motivo }),
  pianificaGiro: (req: { ids: number[]; data: string; ora_inizio?: string; durata_min?: number }) =>
    api.post('/work-orders/pianifica-giro', req),
  salvaOrdineGiro: (req: { ordini: { wo_id: number; ordine: number; ora?: string | null }[] }) =>
    api.post('/work-orders/salva-ordine-giro', req),
  byFrazionario: (frazionario: string) =>
    api.get(`/work-orders/by-frazionario/${encodeURIComponent(frazionario)}`),
  // Anteprime: non scrivono sul DB, si applica poi con applicaCorrezioni
  riparaVerificaPdf: (ids: number[]) =>
    api.post('/work-orders/ripara-verifica-pdf', { ids }),
  riparaCapProvincia: (ids: number[], afterId = 0) =>
    api.post('/work-orders/ripara-cap-provincia', { ids, after_id: afterId }),
  correggiCodiceSito: (ids: number[]) =>
    api.post('/work-orders/correggi-codice-sito', { ids }),
  correggiNomeSito: (ids: number[]) =>
    api.post('/work-orders/correggi-nome-sito', { ids }),
  inviaEmailAccessi: (data: {
    wo_id: number; codice_sito: string; denominazione_sito: string
    regione: string; provincia: string; comune: string; indirizzo: string
    data_appuntamento: string; slot_orario: string; tecnico_nome: string
  }) => api.post('/work-orders/invia-email-accessi', data),
  exportPianificazione: (dataDa: string, dataA: string, committente_id?: number, tecnicoIds?: number[], regioni?: string[]) =>
    api.get('/work-orders/export-pianificazione', {
      params: {
        data_da: dataDa, data_a: dataA, committente_id,
        tecnico_id: tecnicoIds?.length ? tecnicoIds : undefined,
        regione: regioni?.length ? regioni : undefined,
      },
      paramsSerializer: { indexes: null },
      responseType: 'arraybuffer',
    }),
  deleteOne: (id: number) => api.delete(`/work-orders/${id}`),
  deleteAll: () => api.delete('/work-orders'),
  exportXlsx: (anno?: number, mese?: number, regione?: string, tecnico_id?: number) =>
    api.get('/work-orders/export-xlsx', { params: { anno, mese, regione, tecnico_id }, responseType: 'blob' }),
  importXlsx: (file: File, committente_default_id = 1, lotto?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    const lottoQs = lotto ? `&lotto=${encodeURIComponent(lotto)}` : ''
    return api.post(
      `/work-orders/import-xlsx?committente_default_id=${committente_default_id}${lottoQs}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  },
}

export const rapportiniApi = {
  list: (params?: Record<string, unknown>) => api.get('/rapportini', { params }),
  get: (id: number) => api.get(`/rapportini/${id}`),
  create: (data: unknown) => api.post('/rapportini', data),
  update: (id: number, data: unknown) => api.put(`/rapportini/${id}`, data),
  invia: (id: number) => api.put(`/rapportini/${id}/invia`),
  approva: (id: number, inviaEmailCommittente = true, note?: string) =>
    api.put(`/rapportini/${id}/approva`, { invia_email_committente: inviaEmailCommittente, note }),
  rifiuta: (id: number, note?: string) =>
    api.put(`/rapportini/${id}/rifiuta`, null, { params: { note } }),
  delete: (id: number) => api.delete(`/rapportini/${id}`),
  bulkDelete: (ids: number[]) => api.post('/rapportini/bulk-delete', { ids }),
  deleteAll: () => api.delete('/rapportini'),
  inviaCompleto: (fd: FormData, onUploadProgress?: (percent: number) => void) =>
    api.post('/rapportini/invia-completo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (e) => onUploadProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
        : undefined,
    }),
  getPdf: (id: number) => api.get(`/rapportini/${id}/pdf`, { responseType: 'blob' }),
  downloadZip: (anno: number, mese: number) =>
    api.get('/rapportini/download-zip', { params: { anno, mese }, responseType: 'arraybuffer' }),
}

export const checklistApi = {
  list: (params?: { tipologia?: string; committente_id?: number; lotto?: string }) =>
    api.get('/checklist', { params }),
  create: (data: unknown) => api.post('/checklist', data),
  update: (id: number, data: unknown) => api.put(`/checklist/${id}`, data),
  delete: (id: number) => api.delete(`/checklist/${id}`),
}

export const magazzinoApi2 = {
  daFurgone: (targa?: string) => api.get('/magazzino/da-furgone', { params: targa ? { targa } : {} }),
  furgoni: (targa?: string) => api.get('/magazzino/furgoni', { params: targa ? { targa } : {} }),
  furgoniList: () => api.get('/magazzino/furgoni'),
  eliminaFurgone: (targa: string) => api.delete(`/magazzino/furgoni/${encodeURIComponent(targa)}`),
  sostituisciFurgone: (targa: string, nuova_targa: string, sposta_contenuto: boolean) =>
    api.put(`/magazzino/furgoni/${encodeURIComponent(targa)}/sostituisci`, { nuova_targa, sposta_contenuto }),
  richiediTrasferimento: (data: { tipo: string; item_id: number; a_targa: string | null; quantita: number; note?: string }) =>
    api.post('/magazzino/trasferimenti/richiedi', data),
  trasferimentiInArrivo: () => api.get('/magazzino/trasferimenti/in-arrivo'),
  trasferimentiVersoCentrale: () => api.get('/magazzino/trasferimenti/verso-centrale'),
  confermaTrasferimento: (id: number, azione: 'conferma' | 'rifiuta', note?: string) =>
    api.put(`/magazzino/trasferimenti/${id}/conferma`, { azione, note }),
}

export const magazzinoApi = {
  apparati: (params?: Record<string, unknown>) => {
    const { committenti, ...rest } = (params as any) || {}
    if (committenti && Array.isArray(committenti) && committenti.length > 0) {
      const sp = new URLSearchParams()
      Object.entries(rest).forEach(([k, v]) => { if (v !== undefined && v !== null) sp.append(k, String(v)) })
      committenti.forEach((id: number) => sp.append('committenti', String(id)))
      return api.get('/magazzino/apparati?' + sp.toString())
    }
    return api.get('/magazzino/apparati', { params: rest })
  },
  committentiLista: () => api.get('/magazzino/committenti-lista'),
  getApparato: (id: number) => api.get(`/magazzino/apparati/${id}`),
  updateApparato: (id: number, data: unknown) => api.put(`/magazzino/apparati/${id}`, data),
  traccia: (seriale: string) => api.get(`/magazzino/apparati/${seriale}/traccia`),
  createApparato: (data: unknown) => api.post('/magazzino/apparati', data),
  deleteApparato: (id: number) => api.delete(`/magazzino/apparati/${id}`),
  deleteAllApparati: () => api.delete('/magazzino/apparati'),
  assegna: (id: number, data: unknown) => api.put(`/magazzino/apparati/${id}/assegna`, data),
  articoli: (params?: Record<string, unknown>) => api.get('/magazzino/articoli', { params }),
  createArticolo: (data: unknown) => api.post('/magazzino/articoli', data),
  updateArticolo: (id: number, data: unknown) => api.put(`/magazzino/articoli/${id}`, data),
  deleteArticolo: (id: number) => api.delete(`/magazzino/articoli/${id}`),
  createMovimento: (data: unknown) => api.post('/magazzino/movimenti', data),
  sposta: (data: { tipo: string; id: number; destinazione: string; sorgente?: string; quantita?: number }) =>
    api.post('/magazzino/sposta', data),
  spostaAppararatiBulk: (ids: number[], destinazione: string) =>
    api.post('/magazzino/sposta-bulk', { ids, destinazione }),
  movimento: (data: { tipo: string; id: number; destinazione: string; sorgente?: string; quantita?: number }) =>
    api.post('/magazzino/sposta', data),
  ubicazioni: () => api.get('/magazzino/ubicazioni'),
  discrepanze: () => api.get('/magazzino/discrepanze'),
  allineaRapportino: (data: { kit_id?: number; apparato_id?: number }) => api.post('/magazzino/discrepanze/allinea-rapportino', data),
  risolviDiscrepanze: () => api.post('/magazzino/discrepanze/risolvi-tutto'),
  previewRiconcilia: () => api.get('/magazzino/riconcilia-rapportini'),
  eseguiRiconcilia: () => api.post('/magazzino/riconcilia-rapportini'),
  previewRiconciliaMateriali: () => api.get('/magazzino/riconcilia-materiali'),
  eseguiRiconciliaMateriali: () => api.post('/magazzino/riconcilia-materiali'),
  stats: () => api.get('/magazzino/stats'),
  exportXlsx: () => api.get('/magazzino/export-xlsx', { responseType: 'blob' }),
  importCsv: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/magazzino/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  importXlsx: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/magazzino/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}

export const fatturazioneApi = {
  list: (params?: Record<string, unknown>) => api.get('/fatturazione', { params }),
  daFatturare: (committente_id?: number, anno?: number, mese?: number, includi_non_completate?: boolean) =>
    api.get('/fatturazione/da-fatturare', { params: { committente_id, anno, mese, includi_non_completate } }),
  produzione: (anno?: number, mese?: number, committente_id?: number) =>
    api.get('/fatturazione/produzione', { params: { anno, mese, committente_id } }),
  create: (data: unknown) => api.post('/fatturazione', data),
  emetti: (id: number) => api.put(`/fatturazione/${id}/emetti`),
  pagata: (id: number, data_pagamento?: string, riferimento?: string) =>
    api.put(`/fatturazione/${id}/pagata`, null, { params: { data_pagamento, riferimento } }),
  stats: (anno?: number) => api.get('/fatturazione/stats', { params: { anno } }),
  delete: (id: number) => api.delete(`/fatturazione/${id}`),
  deleteAll: () => api.delete('/fatturazione'),
  pdf: (id: number) => api.get(`/fatturazione/${id}/pdf`, { responseType: 'blob' }),
  aggiungiPratiche: (salId: number, woIds: number[]) =>
    api.post(`/fatturazione/${salId}/aggiungi-pratiche`, { wo_ids: woIds }),
  emittiSal: (salId: number, destinatari: string[], corpo: string) =>
    api.post(`/fatturazione/${salId}/emetti-sal`, { destinatari, corpo }),
  aggiornaSalOra: () => api.post('/fatturazione/aggiorna-sal-ora'),
  update: (id: number, data: Record<string, unknown>) => api.patch(`/fatturazione/${id}`, data),
  scadenzario: (committente_id?: number) =>
    api.get('/fatturazione/scadenzario', { params: { committente_id } }),
  creditControl: (anno?: number) =>
    api.get('/fatturazione/credit-control', { params: { anno } }),
  reportIva: (anno?: number) =>
    api.get('/fatturazione/report-iva', { params: { anno } }),
  noteCredito: {
    list: (fattura_id?: number, committente_id?: number) =>
      api.get('/fatturazione/note-credito', { params: { fattura_id, committente_id } }),
    create: (data: { fattura_id: number; numero: string; data: string; importo: number; motivo?: string; note?: string }) =>
      api.post('/fatturazione/note-credito', data),
    delete: (id: number) => api.delete(`/fatturazione/note-credito/${id}`),
  },
  inviaSolleciti: () => api.post('/fatturazione/invia-solleciti'),
}

export const configApi = {
  getSmtp:          () => api.get('/config/smtp'),
  updateSmtp:       (data: Record<string, string>) => api.put('/config/smtp', data),
  testSmtp:         (data: Record<string, string>) => api.post('/config/smtp/test', data),
  getNotifiche:     () => api.get('/config/notifiche'),
  updateNotifiche:  (data: { email_verbali_firmati: string }) => api.put('/config/notifiche', data),
  getWhatsappRapportini:    () => api.get('/config/whatsapp-rapportini'),
  updateWhatsappRapportini: (data: { destinatari: string }) => api.put('/config/whatsapp-rapportini', data),
  getDiarie:        () => api.get('/config/trasferte-diarie'),
  saveDiarie:       (data: Record<string, number>) => api.put('/config/trasferte-diarie', data),
  getCommesse:      () => api.get('/config/commesse-trasferta'),
  createCommessa:   (data: { codice: string; ragione_sociale: string }) => api.post('/config/commesse-trasferta', data),
  updateCommessa:   (id: number, data: { codice: string; ragione_sociale: string }) => api.put(`/config/commesse-trasferta/${id}`, data),
  deleteCommessa:   (id: number) => api.delete(`/config/commesse-trasferta/${id}`),
  deleteAllCommesse:() => api.delete('/config/commesse-trasferta'),
  importCommesse:   (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/config/commesse-trasferta/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  exportCommesse:   () => api.get('/config/commesse-trasferta/export-xlsx', { responseType: 'blob' }),
  getKitSiglaLotto2:    () => api.get('/config/kit-sigla-lotto2'),
  updateKitSiglaLotto2: (sigla: string) => api.put('/config/kit-sigla-lotto2', { sigla }),
  getRedLicenza: () => api.get('/config/red-licenza'),
  setRedLicenza: (bloccato: boolean) => api.put(`/config/red-licenza?bloccato=${bloccato}`),
  getRedSolaLettura: () => api.get('/config/red-sola-lettura'),
  setRedSolaLettura: (attivo: boolean) => api.put(`/config/red-sola-lettura?attivo=${attivo}`),
  get2faDerogaTecnici: () => api.get('/config/2fa-deroga-tecnici'),
  set2faDerogaTecnici: (userIds: number[]) => api.put('/config/2fa-deroga-tecnici', { user_ids: userIds }),
  getBloccoAccessoNotturno: () => api.get('/config/blocco-accesso-notturno'),
  setBloccoAccessoNotturno: (data: { abilitato: boolean; giorni: Record<string, { attivo: boolean; da: string; a: string }> }) =>
    api.put('/config/blocco-accesso-notturno', data),
}

export const orariAperturaApi = {
  import: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/orari-apertura/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  getOrari: (frazionario: string, data: string) =>
    api.get(`/orari-apertura/${encodeURIComponent(frazionario)}/${data}`),
  history: () => api.get('/orari-apertura/history'),
  deleteBatch: (id: number) => api.delete(`/orari-apertura/history/${id}`),
  aggiornaPratiche: () => api.post('/orari-apertura/aggiorna-pratiche'),
}

export const backupApi = {
  lista:      () => api.get('/backup'),
  esegui:     (tipo: 'dati' | 'completo') => api.post('/backup/esegui', { tipo }),
  ripristina: (id: number, cosa: 'dati' | 'file' | 'tutto') =>
                api.post(`/backup/${id}/ripristina`, { cosa }),
  elimina:    (id: number) => api.delete(`/backup/${id}`),
  download:   (id: number) =>
                api.get(`/backup/${id}/download`, { responseType: 'blob' }),
  gdriveConfig:       () => api.get('/backup/gdrive/config'),
  gdriveSalvaConfig:  (data: { client_id: string; client_secret?: string }) => api.put('/backup/gdrive/config', data),
  gdriveAuthorizeUrl: () => api.get('/backup/gdrive/authorize-url'),
  gdriveTest:         () => api.post('/backup/gdrive/test'),
  gdriveTokenRclone:  (data: { codice: string; cartella?: string }) => api.post('/backup/gdrive/token-rclone', data),
  gdriveDisconnect:   () => api.post('/backup/gdrive/disconnect'),
}


export const kitApi = {
  list: (params?: { attivo?: boolean; stato?: string; search?: string }) =>
    api.get('/kit', { params }),
  get: (id: number) => api.get(`/kit/${id}`),
  create: (data: { nome: string; descrizione?: string }) => api.post('/kit', data),
  update: (id: number, data: { nome?: string; descrizione?: string; attivo?: boolean }) =>
    api.put(`/kit/${id}`, data),
  delete: (id: number) => api.delete(`/kit/${id}`),
  bulkDelete: (ids: number[]) => api.delete('/kit/bulk', { data: { ids } }),
  bulkTransfer: (ids: number[], a_ubicazione: string) =>
    api.post('/kit/bulk-transfer', { ids, a_ubicazione }),
  addMateriale: (kitId: number, data: { apparato_id?: number; articolo_id?: number; quantita: number }) =>
    api.post(`/kit/${kitId}/materiali`, data),
  removeMateriale: (kitId: number, kmId: number) =>
    api.delete(`/kit/${kitId}/materiali/${kmId}`),
  reimpostaStato: (kitId: number) =>
    api.post(`/kit/${kitId}/reimposta-stato`),
  allineaStato: (kitId: number) =>
    api.post(`/kit/${kitId}/allinea-stato`),
  segnaInstallato: (kitId: number) =>
    api.post(`/kit/${kitId}/segna-installato`),
  discharge: (kitId: number, data: { work_order_id?: number }) =>
    api.post(`/kit/${kitId}/discharge`, data),
  transfer: (kitId: number, data: { da_ubicazione?: string; a_ubicazione: string }) =>
    api.post(`/kit/${kitId}/transfer`, data),
  transferHistory: (kitId: number) => api.get(`/kit/${kitId}/transfer-history`),
  exportXlsx: () => api.get('/kit/export/xlsx', { responseType: 'blob' }),
  importXlsx: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/kit/import/xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  tracciaSearch: (q: string) => api.get('/magazzino/traccia-apparato', { params: { q } }),
}

export const beniApi = {
  lista:    (tecnicoId: number) =>
              api.get(`/tecnici/${tecnicoId}/beni`),
  crea:     (tecnicoId: number, data: {
              descrizione: string; seriale?: string;
              quantita: number; unita: string;
              data_consegna: string; note?: string;
              categoria?: string; campi_extra?: Record<string, string>;
            }) => api.post(`/tecnici/${tecnicoId}/beni`, data),
  aggiorna: (tecnicoId: number, beneId: number, data: Record<string, unknown>) =>
              api.put(`/tecnici/${tecnicoId}/beni/${beneId}`, data),
  elimina:  (tecnicoId: number, beneId: number) =>
              api.delete(`/tecnici/${tecnicoId}/beni/${beneId}`),
  pdf:      (tecnicoId: number) =>
              api.get(`/tecnici/${tecnicoId}/beni/pdf`, { responseType: 'blob' }),
  uploadFoto: (tecnicoId: number, beneId: number, file: File) => {
    const fd = new FormData()
    fd.append('foto', file)
    return api.post(`/tecnici/${tecnicoId}/beni/${beneId}/foto`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  eliminaFoto: (tecnicoId: number, beneId: number, fotoIndex: number) =>
    api.delete(`/tecnici/${tecnicoId}/beni/${beneId}/foto`, { params: { foto_index: fotoIndex } }),
}

export const verbaliApi = {
  listaAssegnazioni:   (tecnicoId: number) =>
                         api.get(`/assegnazioni-beni/${tecnicoId}`),
  creaAssegnazione:    (data: { tecnico_id: number; data_assegnazione: string; note_admin?: string }) =>
                         api.post('/assegnazioni-beni', data),
  inviaVerbale:        (assegnazioneId: number) =>
                         api.post(`/assegnazioni-beni/${assegnazioneId}/invia-verbale`),
  scaricaFirmato:      (assegnazioneId: number) =>
                         api.get(`/assegnazioni-beni/${assegnazioneId}/verbale-firmato`, { responseType: 'blob' }),
  scaricaBozza:        (assegnazioneId: number) =>
                         api.get(`/assegnazioni-beni/${assegnazioneId}/verbale-bozza`, { responseType: 'blob' }),
  eliminaAssegnazione: (assegnazioneId: number) =>
                         api.delete(`/assegnazioni-beni/${assegnazioneId}`),
  riapriFirmato:       (assegnazioneId: number) =>
                         api.post(`/assegnazioni-beni/${assegnazioneId}/riapri`),
  creaRiconsegna:      (assegnazioneId: number) =>
                         api.post(`/assegnazioni-beni/${assegnazioneId}/crea-riconsegna`),
  verificaToken:       (token: string) =>
                         api.get(`/verbali/verifica-token/${token}`),
  firmaVerbale:        (token: string, firmaBlob: Blob) => {
    const fd = new FormData()
    fd.append('firma_canvas', firmaBlob, 'firma.png')
    return api.post(`/verbali/firma/${token}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  mioVerbalePendente:  () => api.get('/verbali/mio-verbale-pendente'),
}

export const menuPermissionsApi = {
  allMenus: () => api.get('/menu-permissions/menus'),
  myMenus: () => api.get('/menu-permissions/my-menus'),
  getUserMenus: (userId: number) => api.get(`/menu-permissions/user/${userId}`),
  updateUserMenus: (userId: number, permissions: { menu_id: string; enabled: boolean }[]) =>
    api.put(`/menu-permissions/user/${userId}`, { permissions }),
  matrix: () => api.get('/menu-permissions/matrix'),
}

export const commesseApi = {
  list: (params?: Record<string, unknown>) => api.get('/commesse', { params }),
  create: (data: unknown) => api.post('/commesse', data),
  get: (id: number) => api.get(`/commesse/${id}`),
  update: (id: number, data: unknown) => api.put(`/commesse/${id}`, data),
  delete: (id: number) => api.delete(`/commesse/${id}`),
  uploadContratto: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/commesse/${id}/upload-contratto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  cantieri: (commessaId: number) => api.get(`/commesse/${commessaId}/cantieri`),
  createCantiere: (commessaId: number, data: unknown) => api.post(`/commesse/${commessaId}/cantieri`, data),
  rapportini: (id: number) => api.get(`/commesse/${id}/rapportini`),
}

export const rapportinoCommessaApi = {
  getPdf: (id: number) => api.get(`/assegnazioni/rapportino-commessa/${id}/pdf`, { responseType: 'blob' }),
}

export const cantieriApi = {
  list: (params?: Record<string, unknown>) => api.get('/cantieri', { params }),
  nearest: (lat: number, lng: number, radius?: number) =>
    api.get('/cantieri/nearest', { params: { latitude: lat, longitude: lng, radius } }),
  get: (id: number) => api.get(`/cantieri/${id}`),
  update: (id: number, data: unknown) => api.put(`/cantieri/${id}`, data),
  delete: (id: number) => api.delete(`/cantieri/${id}`),
}

export const assegnazioniCantiereApi = {
  list: (params?: Record<string, unknown>) => api.get('/assegnazioni', { params }),
  create: (data: unknown) => api.post('/assegnazioni', data),
  get: (id: number) => api.get(`/assegnazioni/${id}`),
  update: (id: number, data: unknown) => api.put(`/assegnazioni/${id}`, data),
  delete: (id: number) => api.delete(`/assegnazioni/${id}`),
  inviaNotifica: (id: number) => api.post(`/assegnazioni/${id}/invia-notifica`),
  inviaNotificheBulk: (ids: number[]) => api.post('/assegnazioni/invia-notifiche-bulk', { ids }),
  inviaWhatsappTecnico: (id: number) => api.post(`/assegnazioni/${id}/invia-whatsapp-tecnico`),
  inviaWhatsappGruppo: (id: number) => api.post(`/assegnazioni/${id}/invia-whatsapp-gruppo`),
  inviaEmailTecnico: (id: number) => api.post(`/assegnazioni/${id}/invia-email-tecnico`),
  presaConsegna: (id: number, data: { km_presa_consegna: number; carburante_presa_consegna: string; danni_presa_consegna?: string; foto_danni_presa_consegna?: unknown[] }) =>
    api.post(`/assegnazioni/${id}/presa-consegna`, data),
  riconsegna: (id: number, data: { km_riconsegna: number; carburante_riconsegna: string; danni_riconsegna?: string; foto_danni_riconsegna?: unknown[] }) =>
    api.post(`/assegnazioni/${id}/riconsegna`, data),
  terminaAttivita: (id: number, descrizione_attivita: string) =>
    api.post(`/assegnazioni/${id}/termina-attivita`, { descrizione_attivita }),
  reportPdf: (params?: Record<string, unknown>) =>
    api.get('/assegnazioni/report-pdf', { params, responseType: 'blob' }),
  caricoTecnici: (data_da: string, data_a: string) =>
    api.get('/assegnazioni/carico-tecnici', { params: { data_da, data_a } }),
  impostazioni: () => api.get('/assegnazioni/impostazioni'),
  salvaImpostazioni: (email_rapportino: string, email_danni: string) =>
    api.put('/assegnazioni/impostazioni', { email_rapportino, email_danni }),
}

export const speseApi = {
  tipi: () => api.get('/spese/tipi'),
  createTipo: (data: unknown) => api.post('/spese/tipi', data),
  list: (params?: Record<string, unknown>) => api.get('/spese', { params }),
  create: (data: unknown) => api.post('/spese', data),
  get: (id: number) => api.get(`/spese/${id}`),
  uploadRicevuta: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/spese/${id}/upload-ricevuta`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  approva: (id: number) => api.put(`/spese/${id}/approva`),
  rifiuta: (id: number, motivo: string) => api.put(`/spese/${id}/rifiuta`, null, { params: { motivo } }),
  segnaPagata: (id: number) => api.put(`/spese/${id}/segna-pagata`),
  delete: (id: number) => api.delete(`/spese/${id}`),
}

export const economiaApi = {
  tariffe: (params?: Record<string, unknown>) => api.get('/economia/tariffe', { params }),
  createTariffa: (data: unknown) => api.post('/economia/tariffe', data),
  deleteTariffa: (id: number) => api.delete(`/economia/tariffe/${id}`),
  reportCommessa: (commessaId: number) => api.get(`/economia/commessa/${commessaId}/report`),
  riepilogoMensile: (anno: number, mese: number) =>
    api.get('/economia/riepilogo-mensile', { params: { anno, mese } }),
}

export const presenzeApi = {
  list: (params?: Record<string, unknown>) => api.get('/presenze', { params }),
  create: (data: unknown) => api.post('/presenze', data),
  invia: (id: number) => api.put(`/presenze/${id}/invia`),
  approva: (id: number, note?: string) => api.put(`/presenze/${id}/approva`, null, { params: note ? { note } : undefined }),
  rifiuta: (id: number, note?: string) => api.put(`/presenze/${id}/rifiuta`, null, { params: note ? { note } : undefined }),
  riepilogo: (tecnicoId: number, anno: number, mese: number) =>
    api.get(`/presenze/riepilogo/${tecnicoId}`, { params: { anno, mese } }),
  timbraturaInCantiere: (lat: number, lng: number) =>
    api.post('/presenze/timbratura-in-cantiere', { lat, lng }),
  delete: (id: number) => api.delete(`/presenze/${id}`),
}

export const impostazioniApi = {
  getLoghi: () => api.get('/impostazioni/loghi'),
  previewUrl: (tipo: string) => `/api/impostazioni/loghi/${tipo}/preview`,
  uploadLogo: (tipo: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/impostazioni/loghi/${tipo}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  deleteLogo: (tipo: string) => api.delete(`/impostazioni/loghi/${tipo}`),
}


export const trasfertaApi = {
  crea: (data: any) => api.post('/trasferte', data),
  update: (id: number, data: any) => api.put(`/trasferte/${id}`, data),
  creaBozza: (data: any) => api.post('/trasferte/bozza', data),
  patchNrCommessa: (id: number, nr_commessa: string) => api.patch(`/trasferte/${id}/nr-commessa`, { nr_commessa }),
  lista: () => api.get('/trasferte'),
  get: (id: number) => api.get(`/trasferte/${id}`),
  pdf: (id: number) => api.get(`/trasferte/${id}/pdf`, { responseType: 'arraybuffer' }),
  elimina: (id: number) => api.delete(`/trasferte/${id}`),
  eliminaBulk: (ids: number[]) => api.post('/trasferte/bulk-delete', { ids }),
  impostazioni: () => api.get('/trasferte/impostazioni'),
  salvaImpostazioni: (email_destinazione: string, whatsapp_referente?: string, email_notifiche_eventi?: string, whatsapp_notifiche_eventi?: string) =>
    api.put('/trasferte/impostazioni', { email_destinazione, whatsapp_referente, email_notifiche_eventi, whatsapp_notifiche_eventi }),
  autocomplete: (campo: string, q: string) => api.get(`/trasferte/autocomplete/${campo}`, { params: { q } }),
  getTecnicoTarga: (nome: string) => api.get('/trasferte/tecnico-targa', { params: { nome } }),
  notifichePendenti: () => api.get('/trasferte/notifiche-pendenti'),
  inviaBozza: (id: number) => api.post(`/trasferte/${id}/invia`),
  segnaLetta: (id: number, tipo: string) => api.post(`/trasferte/${id}/segna-letta/${tipo}`),
  downloadDistinta: (id: number, tipo: 'dipendenti' | 'albergo', riga_id?: number) =>
    api.get(`/trasferte/${id}/distinta/${tipo}`, {
      responseType: 'blob',
      ...(riga_id !== undefined ? { params: { riga_id } } : {}),
    }),
  uploadDistintaAlbergo: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('distinta', file)
    return api.post(`/trasferte/${id}/distinta/albergo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  uploadDistintaDipendenti: (id: number, rigaId: number, file: File, iban?: string) => {
    const fd = new FormData()
    fd.append('distinta', file)
    if (iban) fd.append('iban', iban)
    return api.post(`/trasferte/${id}/distinta/dipendenti/${rigaId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  sospendiEscalation: (id: number, tipo: 'dipendenti' | 'albergo', data: { fino_al?: string; indefinita: boolean; nota?: string }) =>
    api.put(`/trasferte/${id}/escalation/${tipo}/sospendi`, data),
  riprendiEscalation: (id: number, tipo: 'dipendenti' | 'albergo') =>
    api.put(`/trasferte/${id}/escalation/${tipo}/riprendi`),
  fatturaCortesiaMie: () => api.get('/trasferte/fattura-cortesia/mie'),
  fatturaCortesiaStato: () => api.get('/trasferte/fattura-cortesia/stato'),
  uploadFatturaCortesia: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/trasferte/${id}/fattura-cortesia`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  posticipaFatturaCortesia: (id: number, nuova_scadenza: string) =>
    api.post(`/trasferte/${id}/fattura-cortesia/posticipa`, { nuova_scadenza }),
  sospendiFatturaCortesia: (id: number, data: { fino_al?: string; indefinita: boolean; nota?: string }) =>
    api.post(`/trasferte/${id}/fattura-cortesia/sospendi`, data),
  riprendiFatturaCortesia: (id: number) => api.post(`/trasferte/${id}/fattura-cortesia/riprendi`),
  sbloccaFatturaCortesia: (id: number) => api.post(`/trasferte/${id}/fattura-cortesia/sblocca`),
}

export const trasfertaReportApi = {
  commesse:  (params?: Record<string, unknown>) => api.get('/trasferte/report/commesse', { params }),
  operatori: (params?: Record<string, unknown>) => api.get('/trasferte/report/operatori', { params }),
  alberghi:  (params?: Record<string, unknown>) => api.get('/trasferte/report/alberghi', { params }),
  timeline:  (params?: Record<string, unknown>) => api.get('/trasferte/report/timeline', { params }),
  exportXlsx: (params?: Record<string, unknown>) =>
    api.get('/trasferte/report/export/xlsx', { params, responseType: 'blob' }),
  exportPdf: (params?: Record<string, unknown>) =>
    api.get('/trasferte/report/export/pdf', { params, responseType: 'blob' }),
}

export const chatApi = {
  canali: () => api.get('/chat/canali'),
  creaCanale: (data: { tipo: string; nome?: string; partecipanti_ids: number[] }) =>
    api.post('/chat/canali', data),
  messaggi: (canaleId: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/chat/canali/${canaleId}/messaggi`, { params }),
  invia: (canaleId: number, data: { testo?: string; allegato_url?: string }) =>
    api.post(`/chat/canali/${canaleId}/messaggi`, data),
  segnalLetto: (canaleId: number) => api.put(`/chat/canali/${canaleId}/letto`),
  utenti: () => api.get('/chat/utenti'),
}

export const riordinoApi = {
  proponi: (data: { data_turno: string; ordini: { wo_id: number; numero_progressivo: string; denominazione?: string; ordine_corrente?: number; ordine_proposto: number }[]; nota_tecnico?: string }) =>
    api.post('/riordino', data),
  pendenti: () => api.get('/riordino/pendenti'),
  approva: (id: number, nota_admin?: string) => api.put(`/riordino/${id}/approva`, { nota_admin }),
  rifiuta: (id: number, nota_admin?: string) => api.put(`/riordino/${id}/rifiuta`, { nota_admin }),
}

export const whatsappApi = {
  contacts: () => api.get('/whatsapp/contacts'),
  syncContacts: () => api.post('/whatsapp/contacts/sync'),
  messages: (contactId: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/whatsapp/contacts/${contactId}/messages`, { params }),
  sendMessage: (contactId: number, text: string) =>
    api.post(`/whatsapp/contacts/${contactId}/messages`, { text }),
  markRead: (contactId: number) =>
    api.post(`/whatsapp/contacts/${contactId}/read`),
  sendGroup: (message: string) =>
    api.post('/whatsapp/group/send', { message }),
  sendImage: (phone: string, imageBase64: string, caption?: string) =>
    api.post('/whatsapp/send-image', { phone, image_base64: imageBase64, caption }),
  sendDocument: (phone: string, fileBase64: string, filename?: string, caption?: string, mimetype?: string) =>
    api.post('/whatsapp/send-document', { phone, file_base64: fileBase64, filename, caption, mimetype }),
  status: () => api.get('/whatsapp/status'),
}

export const orePersonaleApi = {
  list: (params?: { tecnico_id?: number; anno?: number; mese?: number; data_da?: string; data_a?: string }) =>
    api.get('/ore-personale', { params }),
  riepilogo: (anno: number, mese: number) =>
    api.get('/ore-personale/riepilogo', { params: { anno, mese } }),
  create: (data: { tecnico_id: number; data: string; ora_inizio: string; ora_fine: string; note?: string }) =>
    api.post('/ore-personale', data),
  update: (id: number, data: { tecnico_id: number; data: string; ora_inizio: string; ora_fine: string; note?: string }) =>
    api.put(`/ore-personale/${id}`, data),
  remove: (id: number) => api.delete(`/ore-personale/${id}`),
}

export interface Mezzo {
  id: number
  targa: string
  modello?: string
}

export const mezziApi = {
  list: (soloAttivi = true) => api.get<Mezzo[]>('/mezzi', { params: { solo_attivi: soloAttivi } }),
  create: (data: any) => api.post('/mezzi', data),
  update: (id: number, data: any) => api.put(`/mezzi/${id}`, data),
  deactivate: (id: number) => api.delete(`/mezzi/${id}`),
  tipiSpesa: () => api.get('/mezzi/tipi-spesa'),
  listSpese: (mezzoId: number, params?: { anno?: number; mese?: number; tipo_spesa?: string }) =>
    api.get(`/mezzi/${mezzoId}/spese`, { params }),
  createSpesa: (mezzoId: number, data: { data: string; tipo_spesa: string; importo: number; km_percorsi?: number; note?: string }) =>
    api.post(`/mezzi/${mezzoId}/spese`, data),
  updateSpesa: (mezzoId: number, spesaId: number, data: any) =>
    api.put(`/mezzi/${mezzoId}/spese/${spesaId}`, data),
  deleteSpesa: (mezzoId: number, spesaId: number) =>
    api.delete(`/mezzi/${mezzoId}/spese/${spesaId}`),
  storicoAssegnazioni: (mezzoId: number) => api.get(`/mezzi/${mezzoId}/storico-assegnazioni`),
}

export const reportPersonaleApi = {
  get: (anno: number, mese: number) =>
    api.get('/economia/report-personale', { params: { anno, mese } }),
}

export const fornitoriApi = {
  list: () => api.get('/fornitori'),
  create: (data: any) => api.post('/fornitori', data),
  update: (id: number, data: any) => api.put(`/fornitori/${id}`, data),
  remove: (id: number) => api.delete(`/fornitori/${id}`),
}

export const rdaApi = {
  list: (stato?: string) => api.get('/rda', { params: stato ? { stato } : undefined }),
  create: (data: any) => api.post('/rda', data),
  avanzaStato: (id: number, stato: string) => api.put(`/rda/${id}/stato`, null, { params: { stato } }),
  remove: (id: number) => api.delete(`/rda/${id}`),
}

export const inventarioApi = {
  listSessioni: () => api.get('/sessioni-inventario'),
  apriSessione: (data: { data: string; note?: string }) => api.post('/sessioni-inventario', data),
  righe: (sid: number) => api.get(`/sessioni-inventario/${sid}/righe`),
  aggiornaRiga: (sid: number, rid: number, data: { conteggio_fisico?: number; approvato?: boolean }) =>
    api.put(`/sessioni-inventario/${sid}/righe/${rid}`, data),
  chiudiSessione: (sid: number) => api.post(`/sessioni-inventario/${sid}/chiudi`),
  eliminaSessione: (sid: number) => api.delete(`/sessioni-inventario/${sid}`),
}

export const valorizzazioneApi = {
  get: () => api.get('/magazzino/valorizzazione'),
}

export const redApi = {
  immobili: {
    list: () => api.get('/red/immobili/'),
    get: (id: string) => api.get(`/red/immobili/${id}`),
    create: (data: unknown) => api.post('/red/immobili/', data),
    update: (id: string, data: unknown) => api.patch(`/red/immobili/${id}`, data),
    avanzaFase: (id: string) => api.post(`/red/immobili/${id}/avanza-fase`, {}),
    delete: (id: string) => api.delete(`/red/immobili/${id}`),
    geocode: (id: string) => api.post(`/red/immobili/${id}/geocode`, {}),
    geocodeMissing: () => api.post('/red/immobili/geocode-missing', {}),
  },
  documenti: {
    listByImmobile: (immobileId: string, fase?: string) =>
      api.get(`/red/documenti/immobile/${immobileId}`, { params: fase ? { fase } : undefined }),
    createPlaceholder: (data: unknown) => api.post('/red/documenti/', data),
    upload: (documentoId: string, file: File) => {
      const fd = new FormData(); fd.append('file', file)
      return api.post(`/red/documenti/${documentoId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    download: (documentoId: string) =>
      api.get(`/red/documenti/${documentoId}/download`, { responseType: 'blob' }),
    delete: (documentoId: string) => api.delete(`/red/documenti/${documentoId}`),
    generaDomandaUsr: (immobileId: string) =>
      api.post(`/red/documenti/genera/domanda-usr/${immobileId}`, {}),
    generaRichiestaSal: (salId: string) =>
      api.post(`/red/documenti/genera/richiesta-erogazione-sal/${salId}`, {}),
    revisiona: (documentoId: string, data: unknown) =>
      api.post(`/red/documenti/${documentoId}/revisiona`, data),
    versiona: (documentoId: string) =>
      api.post(`/red/documenti/${documentoId}/versiona`, {}),
    protocollo: (documentoId: string) =>
      api.post(`/red/documenti/${documentoId}/protocollo`, {}),
    scansionaCartelle: () => api.post('/red/documenti/scansiona-cartelle', {}),
    downloadZip: (ids: string[]) =>
      api.get('/red/documenti/download-zip', { params: { ids: ids.join(',') }, responseType: 'blob' }),
  },
  pagamenti: {
    listByImmobile: (immobileId: string) => api.get(`/red/pagamenti/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/pagamenti/', data),
    update: (id: string, data: unknown) => api.patch(`/red/pagamenti/${id}`, data),
    delete: (id: string) => api.delete(`/red/pagamenti/${id}`),
    syncStati: () => api.post('/red/pagamenti/sync-stati'),
  },
  datiCatastali: {
    listByImmobile: (immobileId: string) => api.get(`/red/immobili/${immobileId}/dati-catastali`),
    create: (immobileId: string, data: unknown) => api.post(`/red/immobili/${immobileId}/dati-catastali`, data),
    update: (voceId: string, data: unknown) => api.put(`/red/dati-catastali/${voceId}`, data),
    delete: (voceId: string) => api.delete(`/red/dati-catastali/${voceId}`),
  },
  appalto: {
    get: (immobileId: string) => api.get(`/red/appalto/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/appalto/', data),
    update: (id: string, data: unknown) => api.patch(`/red/appalto/${id}`, data),
    delete: (id: string) => api.delete(`/red/appalto/${id}`),
    aperturaCantiere: (appaltoId: string, data: { data_apertura: string }) =>
      api.post(`/red/appalto/${appaltoId}/apertura-cantiere-edilconnect`, data),
  },
  gruppi: {
    list: () => api.get('/red/gruppi/'),
    create: (data: unknown) => api.post('/red/gruppi/', data),
    get: (id: string) => api.get(`/red/gruppi/${id}`),
    update: (id: string, data: unknown) => api.patch(`/red/gruppi/${id}`, data),
    delete: (id: string) => api.delete(`/red/gruppi/${id}`),
    listImmobili: (gruppoId: string) => api.get(`/red/gruppi/${gruppoId}/immobili`),
    listCondomini: (gruppoId: string) => api.get(`/red/gruppi/${gruppoId}/condomini`),
    addCondomino: (gruppoId: string, data: unknown) => api.post(`/red/gruppi/${gruppoId}/condomini`, data),
    deleteCondomino: (condominoId: string) => api.delete(`/red/gruppi/condomini/${condominoId}`),
    updateCondomino: (condominoId: string, data: unknown) => api.patch(`/red/gruppi/condomini/${condominoId}`, data),
    creaCondominio: (data: unknown) => api.post('/red/gruppi/condominio', data),
  },
  imeco: {
    listByAppalto: (appaltoId: string) => api.get(`/red/imeco/appalto/${appaltoId}`),
    update: (voceId: string, data: unknown) => api.patch(`/red/imeco/${voceId}`, data),
  },
  computoSOA: {
    listByAppalto: (appaltoId: string) => api.get(`/red/computo-soa/appalto/${appaltoId}`),
    addRiga: (appaltoId: string, data: unknown) => api.post(`/red/computo-soa/appalto/${appaltoId}`, data),
    deleteRiga: (rigaId: string) => api.delete(`/red/computo-soa/${rigaId}`),
  },
  sal: {
    listByImmobile: (immobileId: string) => api.get(`/red/sal/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/sal/', data),
    delete: (id: string) => api.delete(`/red/sal/${id}`),
    benestareDL: (id: string, data: unknown) => api.post(`/red/sal/${id}/benestare-dl`, data),
    richiestaUSR: (id: string, data: unknown) => api.post(`/red/sal/${id}/richiesta-usr`, data),
    erogazioneUSR: (id: string, data: unknown) => api.post(`/red/sal/${id}/erogazione-usr`, data),
  },
  dashboard: {
    kpi: () => api.get('/red/dashboard/kpi'),
    alertAttivi: (giorni = 30) => api.get('/red/dashboard/alert-attivi', { params: { giorni } }),
    salPipeline: () => api.get('/red/dashboard/sal-pipeline'),
    pagamentiScadenza: (giorni = 30) => api.get('/red/dashboard/pagamenti-in-scadenza', { params: { giorni } }),
    accontiSaldiMancanti: () => api.get('/red/dashboard/acconti-saldi-mancanti'),
    grafici: () => api.get('/red/dashboard/grafici'),
  },
  admin: {
    runScheduler: () => api.post('/red/admin/run-scheduler'),
    listScadenze: (immobileId?: string) =>
      api.get('/red/admin/scadenze', { params: immobileId ? { immobile_id: immobileId } : undefined }),
    deleteScadenza: (id: string) => api.delete(`/red/admin/scadenze/${id}`),
    updateScadenza: (id: string, data: { stato?: string; sospesa_fino?: string | null; giorni_preavviso?: number[]; canali?: string[] }) =>
      api.patch(`/red/admin/scadenze/${id}`, data),
  },
  badgeAccessi: {
    listByImmobile: (immobileId: string) => api.get(`/red/badge-accessi/immobile/${immobileId}`),
    report: (immobileId: string) => api.get(`/red/badge-accessi/immobile/${immobileId}/report`),
    create: (immobileId: string, data: unknown) =>
      api.post('/red/badge-accessi/', data, { params: { immobile_id: immobileId } }),
    update: (id: string, data: unknown) => api.patch(`/red/badge-accessi/${id}`, data),
    delete: (id: string) => api.delete(`/red/badge-accessi/${id}`),
    qr: (id: string) => api.get(`/red/badge-accessi/qr/${id}`, { responseType: 'blob' }),
  },
  export: {
    xlsx: (params?: { immobile_id?: string; fase?: string; sheets?: string[] }) =>
      api.get('/red/export/xlsx', {
        params,
        paramsSerializer: { indexes: null },
        responseType: 'blob',
      }),
    master: () =>
      api.get('/red/export/master', { responseType: 'blob' }),
    pdf: (immobileId?: string) =>
      api.get('/red/export/pdf', { params: immobileId ? { immobile_id: immobileId } : undefined, responseType: 'blob' }),
    importXlsx: (file: File) => {
      const fd = new FormData(); fd.append('file', file)
      return api.post('/red/export/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    inviaEmail: (data: { sheets?: string[]; destinatari: string[] }) =>
      api.post('/red/export/invia-email', data),
    inviaEmailAllegato: (data: { file_base64: string; filename?: string; oggetto?: string; corpo?: string; destinatari: string[] }) =>
      api.post('/red/export/invia-email-allegato', data),
  },
  importMaster: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/red/import/master', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  finanziamenti: {
    listByImmobile: (immobileId: string) => api.get(`/red/finanziamenti/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/finanziamenti/', data),
    update: (id: string, data: unknown) => api.patch(`/red/finanziamenti/${id}`, data),
    delete: (id: string) => api.delete(`/red/finanziamenti/${id}`),
  },
  firmaDigitale: {
    listByImmobile: (immobileId: string) => api.get(`/red/firma-digitale/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/firma-digitale/', data),
    update: (id: string, data: unknown) => api.patch(`/red/firma-digitale/${id}`, data),
    invia: (id: string) => api.post(`/red/firma-digitale/${id}/invia`, {}),
    firma: (id: string, firmatario: number) => api.post(`/red/firma-digitale/${id}/firma/${firmatario}`, {}),
    delete: (id: string) => api.delete(`/red/firma-digitale/${id}`),
  },
  fotoCantiere: {
    listByImmobile: (immobileId: string) => api.get(`/red/foto-cantiere/immobile/${immobileId}`),
    upload: (formData: FormData) =>
      api.post('/red/foto-cantiere/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    download: (id: string) => api.get(`/red/foto-cantiere/${id}/download`, { responseType: 'blob' }),
    delete: (id: string) => api.delete(`/red/foto-cantiere/${id}`),
  },
  gantt: {
    lavori: () => api.get('/red/gantt/lavori'),
  },
  importMacerata: {
    run: () => api.post('/red/import/macerata', {}),
  },
  mappa: {
    immobili: (fase?: string) => api.get('/red/mappa/immobili', { params: fase ? { fase } : undefined }),
    coordinate: (immobileId: string) => api.get(`/red/mappa/immobile/${immobileId}`),
    aggiornaCoordinate: (immobileId: string, lat: number, lon: number) =>
      api.patch(`/red/mappa/immobile/${immobileId}`, null, { params: { lat, lon } }),
  },
  opportunita: {
    list: (stato?: string) => api.get('/red/opportunita/', { params: stato ? { stato } : undefined }),
    create: (data: unknown) => api.post('/red/opportunita/', data),
    get: (id: string) => api.get(`/red/opportunita/${id}`),
    update: (id: string, data: unknown) => api.patch(`/red/opportunita/${id}`, data),
    delete: (id: string) => api.delete(`/red/opportunita/${id}`),
    converti: (id: string) => api.post(`/red/opportunita/${id}/converti`, {}),
  },
  praticheEdilizie: {
    listByImmobile: (immobileId: string) => api.get(`/red/pratiche-edilizie/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/pratiche-edilizie/', data),
    update: (id: string, data: unknown) => api.patch(`/red/pratiche-edilizie/${id}`, data),
    delete: (id: string) => api.delete(`/red/pratiche-edilizie/${id}`),
  },
  professionisti: {
    list: (tipo?: string) => api.get('/red/professionisti/', { params: tipo ? { tipo } : undefined }),
    create: (data: unknown) => api.post('/red/professionisti/', data),
    get: (id: string) => api.get(`/red/professionisti/${id}`),
    update: (id: string, data: unknown) => api.patch(`/red/professionisti/${id}`, data),
    delete: (id: string) => api.delete(`/red/professionisti/${id}`),
    listByImmobile: (immobileId: string) => api.get(`/red/professionisti/immobile/${immobileId}`),
    addToImmobile: (immobileId: string, data: unknown) => api.post(`/red/professionisti/immobile/${immobileId}`, data),
    removeFromImmobile: (immobileId: string, assocId: string) =>
      api.delete(`/red/professionisti/immobile/${immobileId}/${assocId}`),
    exportXlsx: () => api.get('/red/professionisti/export/xlsx', { responseType: 'blob' }),
    importXlsx: (file: File) => {
      const fd = new FormData(); fd.append('file', file)
      return api.post('/red/professionisti/import/xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
  },
  rendicontazioni: {
    listByImmobile: (immobileId: string) => api.get(`/red/rendicontazioni/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/rendicontazioni/', data),
    update: (id: string, data: unknown) => api.patch(`/red/rendicontazioni/${id}`, data),
    delete: (id: string) => api.delete(`/red/rendicontazioni/${id}`),
  },
  reportAnac: {
    immobile: (immobileId: string) => api.get(`/red/report-anac/immobile/${immobileId}`),
    portfolio: () => api.get('/red/report-anac/portfolio'),
  },
  simulazione: {
    run: (data: unknown) => api.post('/red/simulazione/', data),
    daImmobile: (immobileId: string, params: unknown) => api.get(`/red/simulazione/immobile/${immobileId}`, { params }),
  },
  valutazioni: {
    listByImmobile: (immobileId: string) => api.get(`/red/valutazioni/immobile/${immobileId}`),
    create: (data: unknown) => api.post('/red/valutazioni/', data),
    update: (id: string, data: unknown) => api.patch(`/red/valutazioni/${id}`, data),
    delete: (id: string) => api.delete(`/red/valutazioni/${id}`),
  },
}

export const praticheUsrApi = {
  list: (gruppo_id?: string) => api.get('/red/pratiche-usr/', { params: { gruppo_id } }),
  create: (data: unknown) => api.post('/red/pratiche-usr/', data),
  update: (id: string, data: unknown) => api.patch(`/red/pratiche-usr/${id}`, data),
  delete: (id: string) => api.delete(`/red/pratiche-usr/${id}`),
}

export const tipologieDocumentoApi = {
  list: () => api.get('/red/tipologie-documento/'),
  listTutte: () => api.get('/red/tipologie-documento/tutte'),
  create: (nome: string) => api.post('/red/tipologie-documento/', { nome }),
  update: (id: string, attivo: boolean) => api.patch(`/red/tipologie-documento/${id}`, { attivo }),
}

export const operatoriAvvisoApi = {
  list: () => api.get('/red/operatori-avviso/'),
  create: (data: { nome: string; email?: string; telefono_whatsapp?: string }) =>
    api.post('/red/operatori-avviso/', data),
  update: (id: string, data: { nome?: string; email?: string; telefono_whatsapp?: string; attivo?: boolean }) =>
    api.patch(`/red/operatori-avviso/${id}`, data),
  delete: (id: string) => api.delete(`/red/operatori-avviso/${id}`),
  getCadenza: () => api.get('/red/operatori-avviso/impostazioni-cadenza'),
  updateCadenza: (data: { giorni_preavviso_default: number[]; canali_default: string[] }) =>
    api.patch('/red/operatori-avviso/impostazioni-cadenza', data),
}

export const agendaApi = {
  list: (params?: { immobile_id?: string; da?: string; a?: string; stato?: string }) =>
    api.get('/red/agenda/', { params }),
  tipiCustom: () => api.get<string[]>('/red/agenda/tipi-custom'),
  create: (data: { immobile_id: string; tipo?: string; titolo: string; testo?: string; data_ora: string; reminder_at?: string; canali?: string[]; destinatari?: string[] }) =>
    api.post('/red/agenda/', data),
  update: (id: string, data: { titolo?: string; testo?: string; data_ora?: string; reminder_at?: string | null; canali?: string[]; destinatari?: string[]; stato?: string }) =>
    api.patch(`/red/agenda/${id}`, data),
  delete: (id: string) => api.delete(`/red/agenda/${id}`),
}

// Istanza dedicata senza interceptor: questa pagina è pubblica (nessun login
// richiesto) e non deve mai essere bloccata dal check readonly né allegare
// un JWT eventualmente presente in localStorage per un'altra sessione XSophia
// aperta nello stesso browser (debug incongruenza sistema RED).
const apiPubblica = axios.create({ baseURL: '/api', headers: { 'Content-Type': 'application/json' } })

export const redConfermaApi = {
  verifica: (token: string) => apiPubblica.get(`/red-conferma/${token}`),
  conferma: (token: string) => apiPubblica.post(`/red-conferma/${token}`),
}

export const timsurveyApi = {
  pratiche: {
    list: (params?: { stato?: string; tecnico_id?: number; search?: string; page?: number; page_size?: number }) =>
      api.get('/timsurvey/pratiche', { params }),
    get: (id: string) => api.get(`/timsurvey/pratiche/${id}`),
    create: (data: unknown) => api.post('/timsurvey/pratiche', data),
    update: (id: string, data: unknown) => api.patch(`/timsurvey/pratiche/${id}`, data),
    parseEmail: (raw_text: string) => api.post('/timsurvey/pratiche/parse-email', { raw_text }),
    assegna: (id: string, tecnico_id: number) => api.post(`/timsurvey/pratiche/${id}/assegna`, { tecnico_id }),
    annulla: (id: string) => api.post(`/timsurvey/pratiche/${id}/annulla`, {}),
    chiudi: (id: string) => api.post(`/timsurvey/pratiche/${id}/chiudi`, {}),
    pdf: (id: string) => api.get(`/timsurvey/pratiche/${id}/pdf`, { responseType: 'blob' }),
  },
  catalogo: {
    casistiche: () => api.get('/timsurvey/catalogo/casistiche'),
    nmu: () => api.get('/timsurvey/catalogo/nmu'),
  },
  verbale: {
    upsert: (praticaId: string, data: unknown) => api.put(`/timsurvey/pratiche/${praticaId}/verbale`, data),
    conferma: (praticaId: string) => api.post(`/timsurvey/pratiche/${praticaId}/verbale/conferma`, {}),
  },
  allegati: {
    upload: (praticaId: string, file: File, tipo: string) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('tipo', tipo)
      return api.post(`/timsurvey/pratiche/${praticaId}/allegati`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    delete: (praticaId: string, allegatoId: string) => api.delete(`/timsurvey/pratiche/${praticaId}/allegati/${allegatoId}`),
  },
  firme: {
    add: (praticaId: string, ruolo: string, nome_firmatario: string, firma_png_base64: string) =>
      api.post(`/timsurvey/pratiche/${praticaId}/firme`, { ruolo, nome_firmatario, firma_png_base64 }),
    delete: (praticaId: string, ruolo: string) => api.delete(`/timsurvey/pratiche/${praticaId}/firme/${ruolo}`),
  },
}

export const rfiApi = {
  listaOrdini: () => api.get('/rfi/ordini'),
  getOrdine: (id: number) => api.get(`/rfi/ordini/${id}`),
  creaOrdine: (data: { oggetto: string; mittente_email?: string; corpo_testo?: string; urgente: boolean; tecnici_ids: number[] }) =>
    api.post('/rfi/ordini', data),
  assegnaOrdine: (id: number, tecnici_ids: number[]) =>
    api.put(`/rfi/ordini/${id}/assegna`, { tecnici_ids }),
  eliminaOrdine: (id: number) => api.delete(`/rfi/ordini/${id}`),

  getRapportino: (id: number) => api.get(`/rfi/rapportini/${id}`),
  salvaGiornata: (rapportinoId: number, data: any) =>
    api.post(`/rfi/rapportini/${rapportinoId}/giornate`, data),
  aggiornaGiornata: (giornataId: number, data: any) =>
    api.put(`/rfi/rapportini/giornate/${giornataId}`, data),
  eliminaGiornata: (giornataId: number) =>
    api.delete(`/rfi/rapportini/giornate/${giornataId}`),
  chiudiRapportino: (rapportinoId: number) =>
    api.post(`/rfi/rapportini/${rapportinoId}/chiudi`),

  uploadAllegato: (ordineId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/rfi/ordini/${ordineId}/allegati`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  listAllegati: (ordineId: number) => api.get(`/rfi/ordini/${ordineId}/allegati`),
  deleteAllegato: (ordineId: number, allegatoId: number) =>
    api.delete(`/rfi/ordini/${ordineId}/allegati/${allegatoId}`),
  downloadAllegatoUrl: (ordineId: number, allegatoId: number) =>
    `/api/rfi/ordini/${ordineId}/allegati/${allegatoId}/download`,
  downloadAllegato: (ordineId: number, allegatoId: number) =>
    api.get(`/rfi/ordini/${ordineId}/allegati/${allegatoId}/download`, { responseType: 'blob' }),

  cercaListino: (q: string) => api.get('/rfi/listino/search', { params: { q } }),
  listinoManodopera: () => api.get('/rfi/listino/manodopera'),
  importaListino: (file: File, listino_nome = 'TOS23') => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('listino_nome', listino_nome)
    return api.post('/rfi/listino/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  nomiListino: () => api.get('/rfi/listino/nomi'),
  listaListino: (params: { listino_nome: string; search?: string; page?: number; per_page?: number }) =>
    api.get('/rfi/listino', { params }),
  aggiornaRigaListino: (id: number, data: { tariffa?: string; descrizione?: string; unita_misura?: string; prezzo?: number }) =>
    api.patch(`/rfi/listino/${id}`, data),

  getGuerrato: (id: number) => api.get(`/rfi/rapportino-guerrato/${id}`),
  aggiornaGuerratoHeader: (id: number, data: any) => api.put(`/rfi/rapportino-guerrato/${id}`, data),
  creaRigaMateriale: (guerratoId: number, data: any) =>
    api.post(`/rfi/rapportino-guerrato/${guerratoId}/righe-materiali`, data),
  aggiornaRigaMateriale: (rigaId: number, data: any) =>
    api.put(`/rfi/rapportino-guerrato/righe-materiali/${rigaId}`, data),
  eliminaRigaMateriale: (rigaId: number) =>
    api.delete(`/rfi/rapportino-guerrato/righe-materiali/${rigaId}`),
  aggiornaRigaManodopera: (rigaId: number, data: any) =>
    api.put(`/rfi/rapportino-guerrato/righe-manodopera/${rigaId}`, data),
  assegnaTariffaManodopera: (rigaId: number, tariffa_codice: string) =>
    api.put(`/rfi/rapportino-guerrato/righe-manodopera/${rigaId}/assegna-tariffa`, { tariffa_codice }),
  downloadPdfGuerrato: (id: number) =>
    api.get(`/rfi/rapportino-guerrato/${id}/pdf`, { responseType: 'blob' }),
}

export const rfiRapportiniApi = {
  list: () => api.get('/rfi/rapportini'),
  invia: (id: number, data: { contatto_ids: number[]; canali: string[] }) =>
    api.post(`/rfi/rapportini/${id}/invia`, data),
}

export const rfiContattiApi = {
  list: () => api.get('/rfi/contatti-avviso'),
  create: (data: { nome: string; email?: string; telefono_whatsapp?: string }) =>
    api.post('/rfi/contatti-avviso', data),
  update: (id: number, data: Partial<{ nome: string; email: string; telefono_whatsapp: string; attivo: boolean }>) =>
    api.patch(`/rfi/contatti-avviso/${id}`, data),
  remove: (id: number) => api.delete(`/rfi/contatti-avviso/${id}`),
}

export const cabApi = {
  parse: (raw: string) => api.post('/cab/parse', { raw }),
  anteprima: (payload: Record<string, unknown>) => api.post('/cab/anteprima', payload),
  crea: (payload: Record<string, unknown>) => api.post('/cab/etichette', payload),
  stampa: (id: string, data: { stampante_id?: number; copie?: number }) =>
    api.post(`/cab/etichette/${id}/stampa`, data),
  esitoStampa: (id: string, esito: 'CONFERMATA' | 'DA_RISTAMPARE') =>
    api.post(`/cab/etichette/${id}/esito-stampa`, { esito }),
  annulla: (id: string, motivo: string) => api.post(`/cab/etichette/${id}/annulla`, { motivo }),
  ristampa: (id: string, data: { motivo: string; stampa?: boolean; stampante_id?: number; copie?: number }) =>
    api.post(`/cab/etichette/${id}/ristampa`, data),
  list: (params?: Record<string, unknown>) => api.get('/cab/etichette', { params }),
  get: (id: string) => api.get(`/cab/etichette/${id}`),
  downloadZpl: (id: string) => api.get(`/cab/etichette/${id}/zpl`, { responseType: 'blob' }),
  downloadSvg: (id: string) => api.get(`/cab/etichette/${id}/svg`, { responseType: 'blob' }),
  downloadPdf: (id: string) => api.get(`/cab/etichette/${id}/pdf`, { responseType: 'blob' }),
  esporta: (da: string, a: string, formato: 'csv' | 'xlsx' = 'csv') =>
    api.get('/cab/esporta', { params: { da, a, formato }, responseType: 'blob' }),
  modelloTrattenuto: (etichetta_id: string) =>
    api.post('/cab/modello-trattenuto', { etichetta_id }, { responseType: 'blob' }),

  nmu: {
    list: () => api.get('/cab/nmu-quartine'),
    create: (data: unknown) => api.post('/cab/nmu-quartine', data),
    update: (id: number, data: unknown) => api.put(`/cab/nmu-quartine/${id}`, data),
    remove: (id: number) => api.delete(`/cab/nmu-quartine/${id}`),
    import: (righe: unknown[]) => api.post('/cab/nmu-quartine/import', righe),
  },
  stampanti: {
    list: () => api.get('/cab/stampanti'),
    create: (data: unknown) => api.post('/cab/stampanti', data),
    update: (id: number, data: unknown) => api.put(`/cab/stampanti/${id}`, data),
    remove: (id: number) => api.delete(`/cab/stampanti/${id}`),
    test: (id: number) => api.post(`/cab/stampanti/${id}/test`),
  },
  impostazioni: {
    get: () => api.get('/cab/impostazioni'),
    update: (data: unknown) => api.put('/cab/impostazioni', data),
  },
}
